class ScentCatalogue extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.scent-catalogue__grid');
    this.panel = this.querySelector('[data-panel]');
    this.panelTitle = this.querySelector('[data-panel-title]');
    this.panelEmpty = this.querySelector('[data-panel-empty]');
    this.clearBtns = Array.from(this.querySelectorAll('[data-clear]'));
    this.revealBtn = this.querySelector('[data-reveal]');
    this.revealRow = this.revealBtn?.closest('.scent-catalogue__reveal-row');
    this.revealCount = this.querySelector('.scent-catalogue__reveal-count');
    this.revealShow = this.querySelector('[data-reveal-show]');
    this.revealHide = this.querySelector('[data-reveal-hide]');

    this.highlightedTarget = parseInt(this.dataset.highlightedCount, 10) || 12;

    this.collect();

    this.clearBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.clear());
    });

    if (this.revealBtn) {
      this.revealBtn.addEventListener('click', () => this.toggleReveal());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dataset.activeNote) this.clear();
    });

    if (this.grid && 'ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(() => this.recomputeVisible());
      this._resizeObserver.observe(this.grid);
    } else {
      this.recomputeVisible();
    }
  }

  // Query the current cards and products, and bind click handlers to any card
  // that doesn't have one yet. Safe to call repeatedly.
  collect() {
    this.cards = Array.from(this.querySelectorAll('[data-note]'));
    this.cardArticles = Array.from(this.querySelectorAll('.scent-catalogue__card'));
    this.products = Array.from(this.querySelectorAll('[data-scent-notes]'));
    this.totalCards = this.cardArticles.length;

    this.cards.forEach((card) => {
      if (card.dataset.bound) return;
      card.dataset.bound = 'true';
      card.addEventListener('click', () => this.toggle(card));
    });
  }

  // Called after more cards/products have been added to the DOM (the section
  // loads further pages of products after first render).
  refresh() {
    this.collect();
    this.dataset.totalCards = this.totalCards;
    if (this.revealCount) this.revealCount.textContent = `(${this.totalCards})`;

    // Products that arrived while a note was selected must respect that filter
    if (this.dataset.activeNote) this.applyFilter(this.dataset.activeNote);

    this.recomputeVisible();
  }

  // Measure the actual number of grid columns the browser is rendering and hide
  // any trailing cards that wouldn't form a full row in the highlighted state.
  recomputeVisible() {
    if (!this.grid || this.cardArticles.length === 0) return;

    if (this.dataset.state === 'expanded') {
      this.cardArticles.forEach((a) => a.removeAttribute('data-collapsed'));
      if (this.revealRow) this.revealRow.hidden = false;
      return;
    }

    const colsValue = getComputedStyle(this.grid).getPropertyValue('grid-template-columns');
    const cols = colsValue.trim().split(/\s+/).filter(Boolean).length;
    if (cols < 1) return;

    // Round to the nearest full row's worth of cards (never below one row).
    const fullRows = Math.max(1, Math.round(this.highlightedTarget / cols));
    const visible = Math.min(fullRows * cols, this.totalCards);

    this.cardArticles.forEach((article, i) => {
      if (i < visible) {
        article.removeAttribute('data-collapsed');
      } else {
        article.setAttribute('data-collapsed', 'true');
      }
    });

    if (this.revealRow) {
      this.revealRow.hidden = visible >= this.totalCards;
    }
  }

  toggle(card) {
    const note = card.dataset.note;
    if (this.dataset.activeNote === note) {
      this.clear();
    } else {
      this.activate(card);
    }
  }

  applyFilter(note) {
    let visibleCount = 0;
    this.products.forEach((p) => {
      const notes = p.dataset.scentNotes.split(/\s+/);
      const match = notes.includes(note);
      p.hidden = !match;
      if (match) visibleCount += 1;
    });

    if (this.panelEmpty) this.panelEmpty.hidden = visibleCount > 0;
  }

  activate(card) {
    const note = card.dataset.note;
    const name = card.querySelector('.scent-catalogue__card-name')?.textContent?.trim() || note;

    this.dataset.activeNote = note;

    this.cards.forEach((c) => {
      c.setAttribute('aria-pressed', c === card ? 'true' : 'false');
    });

    if (this.panelTitle) this.panelTitle.textContent = name;

    this.applyFilter(note);
    if (this.panel) this.panel.hidden = false;

    requestAnimationFrame(() => {
      this.panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  clear() {
    delete this.dataset.activeNote;
    this.cards.forEach((c) => c.setAttribute('aria-pressed', 'false'));
    if (this.panel) this.panel.hidden = true;
  }

  toggleReveal() {
    const expanded = this.dataset.state === 'expanded';
    this.dataset.state = expanded ? 'highlighted' : 'expanded';
    this.revealBtn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (this.revealShow) this.revealShow.hidden = !expanded ? true : false;
    if (this.revealHide) this.revealHide.hidden = !expanded ? false : true;

    if (expanded) {
      // Collapsing back — recompute (respects current column count) and scroll up.
      this.recomputeVisible();
      requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      // Expanding — clear all collapsed flags so every card is visible.
      this.cardArticles.forEach((a) => a.removeAttribute('data-collapsed'));
    }
  }
}

if (!customElements.get('scent-catalogue')) {
  customElements.define('scent-catalogue', ScentCatalogue);
}
